import {memo, useContext} from 'react';

import styled, {css} from 'styled-components';
import {noop} from 'lodash';

import {Link} from '~/shared/router';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexCenter, flexCenterVertically, flexColumn, flexSpace} from '~/shared/theme/FlexLayout';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {media} from '~/shared/theme/media';
import {body13Normal, body14Normal, subheader25Normal} from '~/shared/theme/typography';
import pencilWhiteIconUrl from '~/assets/images/icons/pencil-white.svg';
import pencilBlueIconUrl from '~/assets/images/icons/pencil-blue.svg';
import greenCheckIconUrl from '~/assets/images/icons/green_check.svg';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import Button from '~/shared/components/Button';

import {AddressDropdownContext} from '../context';

const Columns = styled.div`
  display: flex;
  border-bottom: 1px solid ${({theme}) => theme.colors.darkBackground};

  :first-of-type {
    padding-top: 16px;
  }

  ${({hideCurrentLocation}) =>
    !hideCurrentLocation &&
    css`
      &:first-child {
        box-shadow: ${({theme}) => theme.shadows.shadow3};
        margin-bottom: 5px;
      }
    `};
`;

const AddressContainer = styled(Link)`
  justify-content: space-between;
  ${flexCenterVertically};
  flex: 1;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 60px;
  ${flipOnLTR`
    padding-left: 20px;
    padding-right: 30px;
  `}
  cursor: ${({unclickable}) => (unclickable ? 'not-allowed' : 'pointer')};
  background-color: ${({
    themekey,
    theme: {
      addressDropdown: {list},
    },
  }) => list[themekey].backgroundColor};
  transition: opacity 0.25s;

  &:active {
    opacity: 0.5;
  }

  &:hover,
  &:focus {
    background-color: ${({theme}) => theme.addressDropdown.list.hover.backgroundColor};
    color: ${({theme}) => theme.addressDropdown.list.hover.color} !important;

    &&& {
      & ~ button {
        opacity: 1;
      }
    }
  }

  ${media.minLargeTablet`
    ${flipOnLTR`
      padding-left: 68px;
      padding-right: 20px;
    `}
  `}
`;

const AddressColumn = styled.div`
  ${flexColumn};
  flex: 1;
  height: 100%;
  padding-top: 4px;
  overflow: hidden;
`;

const AddressLabel = styled.div.attrs({
  'data-test-id': 'addressTypeLabel',
})`
  display: flex;
  height: 13px;
  ${body13Normal};
  font-weight: 300;
  line-height: 13px;
  color: ${({theme, themekey}) => theme.addressDropdown.list[themekey].labelColor};
  margin-bottom: 5px;
`;

const AddressValue = styled.div`
  ${flexSpace};
  flex: 1;
  height: 20px;
  ${body14Normal};
  line-height: 1.3;
  text-overflow: ellipsis;
  color: ${({theme, themekey}) => theme.addressDropdown.list[themekey].valueColor};
`;

const AddressValueInfo = styled.div.attrs({
  'data-test-id': 'addressLabel',
})`
  overflow: hidden;
  display: flex;
  flex: 1;
  ${({isSingleLine}) =>
    isSingleLine &&
    css`
      white-space: nowrap;
      text-overflow: ellipsis;
    `};
`;

const SelectedIcon = styled(ImageWithAlt).attrs({
  src: greenCheckIconUrl,
})`
  ${subheader25Normal};
  line-height: 25px;
  width: 25px;
  height: 25px;
  color: ${({theme}) => theme.colors.success};
  text-align: center;
`;

const EditButton = styled(Button).attrs({
  'data-test-id': 'navigateToEditAddressBtn',
})`
  width: 60px;
  background-color: ${({theme}) => theme.colors.surface};
  z-index: 1;
  position: relative;
  top: 0;

  &&& {
    ${flexCenter}
    ${media.minLargeTablet`
      background-color: ${({theme}) => theme.addressDropdown.list.button.backgroundColor};
      position: absolute;
      height: 100%;
      ${flipOnLTR`
        left: 0;
        right: auto;
      `}
      opacity: 0;
      &:hover, &:focus {
        opacity: 1;
      }
    `}
  }
`;

const Pencil = styled(ImageWithAlt).attrs(({isMinLargeTablet}) => ({
  src: isMinLargeTablet ? pencilWhiteIconUrl : pencilBlueIconUrl,
}))`
  ${subheader25Normal};
  line-height: 25px;
  width: 25px;
  height: 25px;
`;

const Address = ({
  label,
  value,
  defaultValue = '',
  themekey,
  onEdit,
  children,
  onClick,
  isSelected,
  isSingleLine,
  unclickable,
  hideCurrentLocation,
}) => {
  const {t} = getLocalizationService();
  const {isMinLargeTablet} = useContext(AddressDropdownContext);

  return (
    <Columns hideCurrentLocation={hideCurrentLocation}>
      <AddressContainer
        data-test-id="address-column"
        {...{themekey, unclickable: unclickable ? 1 : 0, onClick: unclickable ? noop : onClick}}
      >
        <AddressColumn>
          <AddressLabel themekey={themekey}>{label}</AddressLabel>
          <AddressValue themekey={themekey}>
            <AddressValueInfo isSingleLine={isSingleLine}>{value || defaultValue}</AddressValueInfo>
            {children}
          </AddressValue>
        </AddressColumn>
        {isSelected && <SelectedIcon alt={t('selected_address')} />}
      </AddressContainer>
      {onEdit && (
        <EditButton
          onClick={e => {
            e.stopPropagation(); // preventing parent's onClick(the button lives inside element with onClick).
            onEdit();
          }}
        >
          <Pencil isMinLargeTablet={isMinLargeTablet} alt={t('edit_address')} />
        </EditButton>
      )}
    </Columns>
  );
};

export default memo(Address);
